// Firebase
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/firebase";

// Interfaces a
import { MetaAdvancedMatching } from "../interfaces/Meta";

export interface MetaUserPayload {
    email?: string;
    phone?: string;
    externalId?: string;
}

interface MetaCookies {
    fbp?: string;
    fbc?: string;
}

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID?.trim() ?? "";
export const DEBUG_META_PIXEL_LOGS = false;
let lastInitSignature = "";
const META_LOG_PREFIX = "[Meta Pixel]";
const META_LOG_STYLE = "color: #ffffff; background: #e08e0a; padding: 2px 6px; border-radius: 4px; font-weight: 600;";
const META_INFO_STYLE = "color: #2b02f4; font-weight: 600;";
const META_SUCCESS_STYLE = "color: #047857; font-weight: 700;";
const META_ERROR_STYLE = "color: #b91c1c; font-weight: 700;";

interface MetaTrackRequest {
    eventName: string;
    eventId: string;
    user?: MetaUserPayload;
    customData?: Record<string, unknown>;
    eventSourceUrl?: string;
}

const metaTrack = httpsCallable<MetaTrackRequest, unknown>(functions, "meta-trackCallable");

export const metaPixelInfo = (...args: unknown[]) => {
    if (!DEBUG_META_PIXEL_LOGS) return;
    console.info(...args);
};

export const metaPixelError = (...args: unknown[]) => {
    if (!DEBUG_META_PIXEL_LOGS) return;
    console.error(...args);
};

const normalizePhoneDigits = (value?: string) => {
    if (!value) return undefined;

    const digits = value.replace(/\D/g, "");
    return digits || undefined;
};

const getEventSourceUrl = () => {
    if (typeof window === "undefined") return undefined;
    return window.location.href;
};

export const getMetaPixelId = () => META_PIXEL_ID;

export const getMetaCookies = (): MetaCookies => {
    if (typeof document === "undefined") {
        return {};
    }

    const getCookieValue = (name: string) => document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`))?.[1];

    return {
        fbp: getCookieValue("_fbp") || undefined,
        fbc: getCookieValue("_fbc") || undefined,
    };
};

export const initMetaPixel = (user?: MetaUserPayload) => {
    if (!META_PIXEL_ID || typeof window === "undefined" || typeof window.fbq !== "function") {
        metaPixelInfo(`%c${META_LOG_PREFIX}%c init skipped`, META_LOG_STYLE, META_INFO_STYLE, {
            hasPixelId: Boolean(META_PIXEL_ID),
            hasWindow: typeof window !== "undefined",
            hasFbq: typeof window !== "undefined" && typeof window.fbq === "function",
            user,
        });
        return;
    }

    const advancedMatching: MetaAdvancedMatching | undefined = user
        ? {
              em: user.email,
              ph: normalizePhoneDigits(user.phone),
              external_id: user.externalId,
          }
        : undefined;

    const signature = JSON.stringify({
        pixelId: META_PIXEL_ID,
        em: advancedMatching?.em ?? "",
        ph: advancedMatching?.ph ?? "",
        external_id: advancedMatching?.external_id ?? "",
    });

    if (signature === lastInitSignature) {
        metaPixelInfo(`%c${META_LOG_PREFIX}%c init skipped duplicate`, META_LOG_STYLE, META_INFO_STYLE, {
            pixelId: META_PIXEL_ID,
            user,
        });
        return;
    }

    metaPixelInfo(`%c${META_LOG_PREFIX}%c init`, META_LOG_STYLE, META_INFO_STYLE, {
        pixelId: META_PIXEL_ID,
        advancedMatching,
    });
    window.fbq("init", META_PIXEL_ID, advancedMatching);
    lastInitSignature = signature;
};

export const trackPixel = (eventName: string, params: Record<string, unknown> = {}, isCustom = false): string => {
    const eventId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}`;

    metaPixelInfo(`%c${META_LOG_PREFIX}%c browser event`, META_LOG_STYLE, META_INFO_STYLE, {
        eventName,
        eventId,
        isCustom,
        params,
        hasFbq: typeof window !== "undefined" && typeof window.fbq === "function",
    });

    if (typeof window !== "undefined" && typeof window.fbq === "function") {
        if (isCustom) {
            window.fbq("trackCustom", eventName, params, { eventID: eventId });
        } else {
            window.fbq("track", eventName, params, { eventID: eventId });
        }
    }

    return eventId;
};

export const trackBoth = async (
    eventName: string,
    params: Record<string, unknown> = {},
    user?: MetaUserPayload,
    isCustom = false
): Promise<string> => {
    const eventId = trackPixel(eventName, params, isCustom);

    try {
        metaPixelInfo(`%c${META_LOG_PREFIX}%c relay event`, META_LOG_STYLE, META_INFO_STYLE, {
            eventName,
            eventId,
            isCustom,
            params,
            user,
            eventSourceUrl: getEventSourceUrl(),
        });
        await metaTrack({
            eventName,
            eventId,
            user,
            customData: params,
            eventSourceUrl: getEventSourceUrl(),
        });
        metaPixelInfo(`%c${META_LOG_PREFIX}%c relay success`, META_LOG_STYLE, META_SUCCESS_STYLE, {
            eventName,
            eventId,
        });
    } catch (error) {
        metaPixelError(`%c${META_LOG_PREFIX}%c relay error`, META_LOG_STYLE, META_ERROR_STYLE, eventName, error);
    }

    return eventId;
};
